import { useState } from "react";

import styled from "styled-components";

import { Combobox, DropdownButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";

const DROPDOWN_WIDTH = "240px";

const TriggerWrap = styled.div`
    width: ${DROPDOWN_WIDTH};
`;

const DomainColorDot = styled.span<{ $color: string }>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${({ $color }) => $color};
    flex-shrink: 0;
`;

interface GapMultiSelectDropdownProps {
    domains: { name: string; color: string }[];
    selected: string[];
    onChange: (domains: string[]) => void;
    "data-automation"?: string;
}

export const GapMultiSelectDropdown = ({
    domains,
    selected = [],
    onChange,
    "data-automation": dataAutomation,
}: GapMultiSelectDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const isMultiSelect = domains.length > 2;

    const handleToggleDomain = (domainName: string) => {
        if (!isMultiSelect) {
            onChange([domainName]);
            setIsOpen(false);
            return;
        }
        const isCurrentlySelected = selected.includes(domainName);
        if (isCurrentlySelected && selected.length === 1) {
            return;
        }
        const next = isCurrentlySelected ? selected.filter((d) => d !== domainName) : [...selected, domainName];
        onChange(next);
    };

    const selectedDomains = domains.filter((d) => selected.includes(d.name));
    const buttonLabel =
        selectedDomains.length > 1
            ? translate("gap.toggle.perspective.selected.count", { count: selectedDomains.length })
            : selectedDomains.map((d) => d.name).join(", ");

    return (
        <Combobox
            data-automation={dataAutomation}
            width={DROPDOWN_WIDTH}
            isOpen={isOpen}
            onOpenChange={setIsOpen}
        >
            <Combobox.Trigger>
                <TriggerWrap>
                    <DropdownButton
                        width={DROPDOWN_WIDTH}
                        hasValue={selectedDomains.length > 0}
                    >
                        {buttonLabel}
                    </DropdownButton>
                </TriggerWrap>
            </Combobox.Trigger>
            <Combobox.Content>
                {domains.map((domain) => {
                    const isSelected = selected.includes(domain.name);
                    const isOnlySelected = isMultiSelect && isSelected && selected.length === 1;
                    return (
                        <Combobox.Option
                            key={domain.name}
                            id={domain.name}
                            textValue={domain.name}
                            isDisabled={isOnlySelected}
                            onClick={() => !isOnlySelected && handleToggleDomain(domain.name)}
                        >
                            <Flex
                                alignItems="center"
                                justifyContent="space-between"
                                gap="8px"
                                width="100%"
                            >
                                <Flex
                                    alignItems="center"
                                    gap="8px"
                                    flex="1 1 auto"
                                    minWidth={0}
                                    overflow="hidden"
                                >
                                    <DomainColorDot $color={domain.color} />
                                    <Combobox.OptionLabel
                                        flex="1"
                                        minWidth={0}
                                        overflow="hidden"
                                        whiteSpace="nowrap"
                                        textOverflow="ellipsis"
                                    >
                                        {domain.name}
                                    </Combobox.OptionLabel>
                                </Flex>
                                {isMultiSelect ? <Combobox.OptionCheckbox isChecked={isSelected} /> : null}
                            </Flex>
                        </Combobox.Option>
                    );
                })}
            </Combobox.Content>
        </Combobox>
    );
};
